import { Link } from 'react-router';
import { timeZoneLabel } from '@ruby-cms/shared';
import { useQuery } from '@tanstack/react-query';
import { Tag } from '@carbon/react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader, ErrorState, BlockSkeleton, EmptyState } from '../components/states';
import { Money, When } from '../components/format';
import { GRAIN_LABEL, PARTIAL_NOTE, PERIOD_WORD, periodTick } from '../components/Analytics';
import { Alerts, Columns, InsightBar, ScopeNote, ShareBar, Tile, ddmm, fmt, grainOf, periodRow, secs, useInsightFilters, type Cmp, type Kind, type Scope } from '../components/Insights';

// Screen 2 of the KPI catalogue: the cashier. Deposits credited and withdrawals paid, the request funnel,
// approval time and withdrawal SLA (nearest-rank p50 / p95), rejections, the waiting queue, top
// depositors, player liability and house net cash (GET /api/admin/insights/cashier).

type Head = {
  from: string; to: string; deposits: string; depositCount: number; depositors: number; avgDeposit: number | null;
  withdrawals: string; withdrawalCount: number; withdrawers: number; avgWithdrawal: number | null; netDeposit: string;
  ftd: number; ftdAmount: string; agentPayouts: string; houseNetCash: string; turnover: string; turnoverToDeposit: number | null; withdrawalToDeposit: number | null;
};
type Day = {
  day: string; deposits: string; depositCount: number; depositBank: string; depositUsdt: string; depositors: number; withdrawals: string; withdrawalCount: number;
  withdrawBank: string; withdrawUsdt: string; netDeposit: string; ftd: number; ftdAmount: string; depositsCreated: number; depositsRejected: number;
  depositsCancelled: number; withdrawalsCreated: number; withdrawalsRejected: number; withdrawalsCancelled: number; rejectionRate: number | null; liability: string | null; partial?: boolean;
};
type Dur = { channel: string | null; n: number; p50: number | null; p95: number | null; withinSla: number | null };
type Funnel = { created: number; claimed: number; open: number; held: number; done: number; rejected: number; cancelled: number; expired: number };
type Cashier = {
  scope: Scope; headline: Head; compare: Cmp<Head>; days: Day[];
  byHour: { hour: number; deposits: string; depositCount: number; withdrawals: string; withdrawalCount: number }[];
  methodMix: { dir: string; channel: string; method: string; count: number; amount: string; share: number | null }[];
  rejection: { dir: string; channel: string; created: number; rejected: number; cancelled: number; rejectionRate: number | null; cancelRate: number | null }[];
  funnel: { nap: Funnel; rut: Funnel };
  approval: { all: Dur; byChannel: Dur[] }; withdrawSla: { all: Dur; byChannel: Dur[] }; sla: { nap: number; rut: number };
  queue: { dir: string; count: number; amount: string; held: number; oldest: string | null; oldestAgeSec: number | null; overSla: number }[];
  aging: { src: string; id: string; userId: number; nick: string | null; dir: string; channel: string; amount: string; status: string; created: string }[];
  topDepositors: { userId: number; nick: string | null; count: number; amount: string }[];
  liability: { now: string; byDay: { day: string; value: string | null }[] };
  alerts: { kind: string; day?: string; value: number }[];
  thresholds: { rejectRatePct: number; withdrawSlaMinutes: number };
};

const DIR: Record<string, string> = { nap: 'Nạp', rut: 'Rút' };
const CHANNEL: Record<string, string> = { bank: 'Ngân hàng', usdt: 'USDT' };
const STATUS: Record<string, string> = { open: 'Đang chờ', held: 'Đã giữ tiền, chờ chi' };

const TILES: [keyof Head, string, Kind, string?][] = [
  ['deposits', 'Tiền nạp', 'money'],
  ['withdrawals', 'Tiền rút đã chi', 'money'],
  ['netDeposit', 'Nạp ròng', 'money', 'Nạp - rút'],
  ['houseNetCash', 'Tiền mặt nhà cái giữ lại', 'money', 'Nạp - rút - chi hoa hồng đại lý'],
  ['depositors', 'Người nạp', 'count'],
  ['avgDeposit', 'Nạp trung bình', 'money'],
  ['ftd', 'Nạp lần đầu (FTD)', 'count'],
  ['ftdAmount', 'Tiền nạp lần đầu', 'money'],
  ['avgWithdrawal', 'Rút trung bình', 'money'],
  ['agentPayouts', 'Chi hoa hồng đại lý', 'money'],
  ['turnoverToDeposit', 'Doanh số / tiền nạp', 'num', 'Mỗi đồng nạp được cược bao nhiêu lần'],
  ['withdrawalToDeposit', 'Rút / nạp', 'pct'],
];

function UserLink({ id, nick }: { id: number; nick: string | null }) {
  const { can } = useAuth();
  return can('users.view') ? <Link className="cell-link" to={`/users/${id}`}>{nick ?? '#' + id}</Link> : <>{nick ?? '#' + id}</>;
}

export function InsightCashierPage() {
  const { can } = useAuth();
  const f = useInsightFilters(['channel']);
  const q = useQuery({ queryKey: ['insights', 'cashier', f.query], queryFn: () => api.get<Cashier>('/api/admin/insights/cashier', f.query) });
  const d = q.data;
  const grain = grainOf(f);
  const word = PERIOD_WORD[grain];
  // one alert for every day over the rejection threshold: a 12-week or 12-month range would stack dozens of them
  const rej = (d?.alerts ?? []).filter((a) => a.kind !== 'withdrawOverSla');
  const worst = rej.reduce<(typeof rej)[number] | null>((m, a) => (!m || a.value > m.value ? a : m), null);
  const last = rej.at(-1);
  const alerts = [
    ...(d?.alerts ?? []).filter((a) => a.kind === 'withdrawOverSla').map((a) => (
      { key: 'sla', title: 'Lệnh rút chờ quá hạn', body: `${a.value} lệnh rút đang chờ quá ${d!.thresholds.withdrawSlaMinutes} phút (hạn xử lý rút trong cấu hình nạp rút).` })),
    ...(rej.length === 1 ? [{ key: 'rej', title: 'Tỷ lệ từ chối cao', body: `Ngày ${ddmm(rej[0]!.day!)} từ chối ${fmt(rej[0]!.value, 'pct')} số yêu cầu tạo trong ngày (ngưỡng ${d!.thresholds.rejectRatePct}%).` }]
      : rej.length > 1 && worst && last ? [{ key: 'rej', title: 'Tỷ lệ từ chối cao',
        body: `${rej.length} ngày trong khoảng này từ chối trên ${d!.thresholds.rejectRatePct}% số yêu cầu tạo trong ngày; cao nhất ngày ${ddmm(worst.day!)} (${fmt(worst.value, 'pct')}), gần nhất ngày ${ddmm(last.day!)} (${fmt(last.value, 'pct')}).` }]
      : []),
  ];
  const mixMax = Math.max(0, ...(d?.methodMix ?? []).map((m) => Number(m.amount)));
  const funnel = (dir: 'nap' | 'rut') => d!.funnel[dir];
  return (
    <div className="page">
      <PageHeader title="Thu ngân" lead={`Nạp đã cộng tiền và rút đã chi theo ngày, ${timeZoneLabel()}, từ lệnh nạp rút của sảnh và các yêu cầu cũ đã đóng; tài khoản thử và nhân viên không tính.`} />
      <InsightBar id="ic" f={f} exportPath="/api/admin/insights/cashier"
        selects={[{ key: 'channel', label: 'Kênh', options: [['', 'Tất cả kênh'], ['bank', 'Ngân hàng'], ['usdt', 'USDT']] }]}
        exportSections={[['days', `Từng ${word}`], ['mix', 'Theo kênh và ngân hàng'], ['queue', 'Lệnh đang chờ'], ['top', 'Người nạp nhiều nhất']]} />
      <ScopeNote scope={d?.scope} />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : d ? (
        <>
          <Alerts items={alerts} />
          <div className="kpis insight-kpis">
            {TILES.map(([k, label, kind, hint], i) => <Tile key={k} label={label} k={k} kind={kind} cur={d.headline} cmp={d.compare} hint={hint} lead={i === 2} />)}
          </div>

          <section className="section panel">
            <div className="panel__head"><h2>Nạp và rút theo {word}</h2></div>
            <Columns title={`Tiền nạp và tiền rút theo ${word}`} kind="money" labels={d.days.map((x) => periodTick(x.day, grain, x.partial))}
              series={[{ name: 'Nạp', values: d.days.map((x) => Number(x.deposits)) }, { name: 'Rút', values: d.days.map((x) => Number(x.withdrawals)) }]} />
            <div className="table-scroll">
              <table className="plain">
                <thead><tr><th>{GRAIN_LABEL[grain]}</th><th className="r">Nạp</th><th className="r">Ngân hàng</th><th className="r">USDT</th><th className="r">Người nạp</th><th className="r">Rút</th><th className="r">Nạp ròng</th><th className="r">FTD</th><th className="r">Yêu cầu tạo</th><th className="r">Từ chối</th><th className="r">Tỷ lệ từ chối</th><th className="r">Số dư người chơi cuối {word}</th></tr></thead>
                <tbody>{[...d.days].reverse().map((x) => (
                  <tr key={x.day}>
                    <td className="num is-title">{periodRow(x.day, grain, x.partial)}</td>
                    <td className="r" data-label="Nạp"><Money v={x.deposits} /> <span className="muted">({x.depositCount})</span></td>
                    <td className="r" data-label="Ngân hàng"><Money v={x.depositBank} /></td>
                    <td className="r" data-label="USDT"><Money v={x.depositUsdt} /></td>
                    <td className="r num" data-label="Người nạp">{x.depositors}</td>
                    <td className="r" data-label="Rút"><Money v={x.withdrawals} /> <span className="muted">({x.withdrawalCount})</span></td>
                    <td className="r" data-label="Nạp ròng"><Money v={x.netDeposit} signed /></td>
                    <td className="r num" data-label="FTD">{x.ftd}</td>
                    <td className="r num" data-label="Yêu cầu tạo">{x.depositsCreated + x.withdrawalsCreated}</td>
                    <td className="r num" data-label="Từ chối">{x.depositsRejected + x.withdrawalsRejected}</td>
                    <td className="r num" data-label="Tỷ lệ từ chối">{fmt(x.rejectionRate, 'pct')}{x.rejectionRate !== null && x.rejectionRate * 100 > d.thresholds.rejectRatePct ? <> <Tag type="red" size="sm">Cao</Tag></> : null}</td>
                    <td className="r" data-label={`Số dư cuối ${word}`}><Money v={x.liability} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {grain !== 'day' ? <p className="muted">Người nạp đếm mỗi tài khoản một lần trong mỗi {word}; số dư là số cuối {word}. {PARTIAL_NOTE}</p> : null}
          </section>

          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Theo giờ trong ngày ({timeZoneLabel()})</h2></div>
              <Columns title="Tiền nạp và rút theo giờ" kind="money" height={200} labels={d.byHour.map((h) => String(h.hour).padStart(2, '0'))}
                hints={d.byHour.map((h) => `${String(h.hour).padStart(2, '0')}:00 đến ${String(h.hour).padStart(2, '0')}:59, ${h.depositCount} lệnh nạp, ${h.withdrawalCount} lệnh rút`)}
                series={[{ name: 'Nạp', values: d.byHour.map((h) => Number(h.deposits)) }, { name: 'Rút', values: d.byHour.map((h) => Number(h.withdrawals)) }]} />
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Theo kênh và ngân hàng</h2></div>
              {d.methodMix.length ? (
                <table className="plain">
                  <thead><tr><th>Chiều</th><th>Kênh</th><th>Ngân hàng / mạng</th><th className="r">Số lệnh</th><th>Tiền</th><th className="r">Tỷ trọng</th></tr></thead>
                  <tbody>{d.methodMix.map((m) => (
                    <tr key={m.dir + m.channel + m.method}>
                      <td data-label="Chiều">{DIR[m.dir]}</td><td data-label="Kênh">{CHANNEL[m.channel] ?? m.channel}</td><td className="is-title">{m.method || '-'}</td>
                      <td className="r num" data-label="Số lệnh">{m.count}</td>
                      <td data-label="Tiền"><ShareBar v={Number(m.amount)} max={mixMax} kind="money"><Money v={m.amount} /></ShareBar></td>
                      <td className="r num" data-label="Tỷ trọng">{fmt(m.share, 'pct')}</td>
                    </tr>
                  ))}</tbody>
                </table>
              ) : <EmptyState title="Không có khoản nạp rút hoàn tất trong khoảng này" />}
            </section>
          </div>

          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Trạng thái yêu cầu tạo trong khoảng</h2></div>
              <div className="table-scroll"><table className="plain">
                <thead><tr><th>Chiều</th><th className="r">Tạo</th><th className="r">Đã báo chuyển</th><th className="r">Đang chờ</th><th className="r">Đã giữ tiền</th><th className="r">Hoàn tất</th><th className="r">Từ chối</th><th className="r">Người chơi hủy</th><th className="r">Hết hạn</th></tr></thead>
                <tbody>{(['nap', 'rut'] as const).map((dir) => { const x = funnel(dir); return (
                  <tr key={dir}>
                    <td className="is-title">{DIR[dir]}</td>
                    <td className="r num" data-label="Tạo">{x.created}</td><td className="r num" data-label="Đã báo chuyển">{x.claimed}</td>
                    <td className="r num" data-label="Đang chờ">{x.open}</td><td className="r num" data-label="Đã giữ tiền">{x.held}</td>
                    <td className="r num" data-label="Hoàn tất">{x.done}</td><td className="r num" data-label="Từ chối">{x.rejected}</td>
                    <td className="r num" data-label="Người chơi hủy">{x.cancelled}</td><td className="r num" data-label="Hết hạn">{x.expired}</td>
                  </tr>
                ); })}</tbody>
              </table></div>
              <table className="plain section">
                <thead><tr><th>Chiều</th><th>Kênh</th><th className="r">Tạo</th><th className="r">Từ chối</th><th className="r">Tỷ lệ từ chối</th><th className="r">Tỷ lệ hủy</th></tr></thead>
                <tbody>{d.rejection.map((r) => (
                  <tr key={r.dir + r.channel}>
                    <td className="is-title">{DIR[r.dir]}</td><td data-label="Kênh">{CHANNEL[r.channel] ?? r.channel}</td>
                    <td className="r num" data-label="Tạo">{r.created}</td><td className="r num" data-label="Từ chối">{r.rejected}</td>
                    <td className="r num" data-label="Tỷ lệ từ chối">{fmt(r.rejectionRate, 'pct')}</td><td className="r num" data-label="Tỷ lệ hủy">{fmt(r.cancelRate, 'pct')}</td>
                  </tr>
                ))}</tbody>
              </table>
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Thời gian xử lý</h2></div>
              <table className="plain">
                <thead><tr><th>Loại</th><th>Kênh</th><th className="r">Số lệnh</th><th className="r">Trung vị (p50)</th><th className="r">p95</th><th className="r">Trong hạn</th></tr></thead>
                <tbody>
                  {[['Duyệt nạp', d.approval, d.sla.nap] as const, ['Chi rút', d.withdrawSla, d.sla.rut] as const].flatMap(([label, g, sla]) => [g.all, ...g.byChannel].map((x) => (
                    <tr key={label + (x.channel ?? 'all')}>
                      <td className="is-title">{label}</td><td data-label="Kênh">{x.channel ? CHANNEL[x.channel] ?? x.channel : 'Tất cả'}</td>
                      <td className="r num" data-label="Số lệnh">{x.n}</td><td className="r num" data-label="p50">{secs(x.p50)}</td><td className="r num" data-label="p95">{secs(x.p95)}</td>
                      <td className="r num" data-label="Trong hạn" title={`Hạn ${sla} phút`}>{fmt(x.withinSla, 'pct')}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
              <p className="muted">Duyệt nạp tính từ lúc tạo lệnh đến lúc CMS quyết định; chi rút tính từ lúc tạo lệnh đến lúc đã chuyển tiền. Hạn: nạp {d.sla.nap} phút, rút {d.sla.rut} phút (cấu hình nạp rút).</p>
            </section>
          </div>

          <section className="section panel">
            <div className="panel__head">
              <h2>Lệnh đang chờ (hiện tại)</h2>
              {can('wallet.view') ? <Link to="/wallet">Mở trang nạp / rút</Link> : null}
            </div>
            <div className="kpis insight-kpis">
              {d.queue.map((x) => (
                <div key={x.dir} className={'kpi' + (x.overSla ? ' insight-tile--warn' : '')}>
                  <span className="kpi__label">{DIR[x.dir]} đang chờ</span>
                  <span className="kpi__value"><span className="num">{x.count}</span> <span className="muted">lệnh</span></span>
                  <span className="kpi__hint"><Money v={x.amount} unit />{x.oldest ? <>, cũ nhất {secs(x.oldestAgeSec)}</> : null}{x.dir === 'rut' && x.overSla ? `, ${x.overSla} lệnh quá hạn` : ''}</span>
                </div>
              ))}
            </div>
            {d.aging.length ? (
              <table className="plain section">
                <thead><tr><th>Mã lệnh</th><th>Người chơi</th><th>Chiều</th><th>Kênh</th><th className="r">Số tiền</th><th>Trạng thái</th><th>Tạo lúc</th></tr></thead>
                <tbody>{d.aging.map((a) => (
                  <tr key={a.src + a.id}>
                    <td className="is-title">{can('wallet.view') ? <Link className="cell-link" to={`${a.dir === 'nap' ? '/wallet/deposits' : '/wallet/withdrawals'}?status=&ref=${encodeURIComponent(a.id)}`}>{a.id}</Link> : a.id}</td>
                    <td data-label="Người chơi"><UserLink id={a.userId} nick={a.nick} /></td>
                    <td data-label="Chiều">{DIR[a.dir]}</td><td data-label="Kênh">{CHANNEL[a.channel] ?? a.channel}</td>
                    <td className="r" data-label="Số tiền"><Money v={a.amount} /></td>
                    <td data-label="Trạng thái">{STATUS[a.status] ?? a.status}</td>
                    <td data-label="Tạo lúc"><When v={a.created} /></td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <EmptyState title="Không có lệnh nào đang chờ" />}
          </section>

          <div className="split split--even section">
            <section className="panel">
              <div className="panel__head"><h2>Người nạp nhiều nhất</h2></div>
              {d.topDepositors.length ? (
                <table className="plain">
                  <thead><tr><th className="r">#</th><th>Người chơi</th><th className="r">Số lần</th><th className="r">Tiền nạp</th></tr></thead>
                  <tbody>{d.topDepositors.map((t, i) => (
                    <tr key={t.userId}><td className="r num muted" data-label="#">{i + 1}</td><td className="is-title"><UserLink id={t.userId} nick={t.nick} /></td>
                      <td className="r num" data-label="Số lần">{t.count}</td><td className="r" data-label="Tiền nạp"><Money v={t.amount} /></td></tr>
                  ))}</tbody>
                </table>
              ) : <EmptyState title="Không có ai nạp trong khoảng này" />}
            </section>
            <section className="panel">
              <div className="panel__head"><h2>Số dư người chơi (nợ phải trả)</h2></div>
              <div className="kpi insight-inline-kpi">
                <span className="kpi__label">Hiện tại</span>
                <span className="kpi__value"><Money v={d.liability.now} unit /></span>
              </div>
              {d.liability.byDay.some((x) => x.value !== null) ? (
                <Columns title={`Số dư người chơi cuối mỗi ${word}`} kind="money" height={180} labels={d.liability.byDay.map((x) => periodTick(x.day, grain, d.days.find((y) => y.day === x.day)?.partial))}
                  series={[{ name: 'Số dư', values: d.liability.byDay.map((x) => (x.value === null ? null : Number(x.value))) }]} />
              ) : <p className="muted">Lịch sử theo {word} chỉ có khi xem toàn hệ thống (không lọc đại lý hay người đã nạp).</p>}
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}
